import { ref, computed, readonly, type Ref } from 'vue'
import Cookies from 'js-cookie'
import apiClient from '../services/api'
import type { TokenPayload, User, UseAuthReturn } from '@/types'
import { env } from '../config/env'

const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000 // 5 minutes in milliseconds

// Local interfaces specific to this composable
interface RefreshTokenResponse {
  access_token: string
  refresh_token?: string
  user?: User
}

interface CookieOptions {
  domain?: string
  secure?: boolean
  sameSite?: 'strict' | 'lax' | 'none'
  expires?: number | Date
  httpOnly?: boolean
}

// Global state
const accessToken: Ref<string | null> = ref(null)
const refreshToken: Ref<string | null> = ref(null)
const user: Ref<User | null> = ref(null)
const isLoading: Ref<boolean> = ref(false)
const error: Ref<string | null> = ref(null)

let refreshPromise: Promise<string> | null = null

// Token validation functions
const validateTokenFormat = (token: string | null): token is string => {
  if (!token || typeof token !== 'string') return false

  const parts = token.split('.')
  if (parts.length !== 3) return false

  try {
    JSON.parse(atob(parts[1]))
    return true
  } catch {
    return false
  }
}

const parseTokenPayload = (token: string): TokenPayload | null => {
  if (!validateTokenFormat(token)) return null

  try {
    return JSON.parse(atob(token.split('.')[1])) as TokenPayload
  } catch (error) {
    console.warn('Failed to parse token payload:', error)
    return null
  }
}

const isTokenExpiringSoon = (token: string): boolean => {
  const payload = parseTokenPayload(token)
  if (!payload || !payload.exp) return true

  const expirationTime = payload.exp * 1000
  const currentTime = Date.now()

  return expirationTime - currentTime < TOKEN_REFRESH_THRESHOLD
}

const clearAuthState = (): void => {
  accessToken.value = null
  refreshToken.value = null
  user.value = null
  error.value = null
  refreshPromise = null
}

export function useAuth(): UseAuthReturn {
  const isAuthenticated = computed(() => {
    return !!accessToken.value && validateTokenFormat(accessToken.value)
  })

  const isTokenExpired = computed(() => {
    if (!accessToken.value) return true

    const payload = parseTokenPayload(accessToken.value)
    if (!payload || !payload.exp) return true

    const currentTime = Math.floor(Date.now() / 1000)
    return payload.exp < currentTime
  })

  const tokenExpiresAt = computed(() => {
    if (!accessToken.value) return null

    const payload = parseTokenPayload(accessToken.value)
    if (!payload || !payload.exp) return null

    return new Date(payload.exp * 1000)
  })

  const getTokenFromCookie = (cookieName: string): string | null => {
    try {
      const token = Cookies.get(cookieName)
      return token || null
    } catch (error) {
      console.warn(`Failed to get cookie ${cookieName}:`, error)
      return null
    }
  }

  const setTokenCookie = (
    cookieName: string,
    token: string,
    options: CookieOptions = {}
  ): void => {
    try {
      const defaultOptions: CookieOptions = {
        domain: env.authCookieDomain,
        secure: env.isProduction,
        sameSite: 'lax',
        ...options,
      }

      Cookies.set(cookieName, token, defaultOptions)
    } catch (error) {
      console.error(`Failed to set cookie ${cookieName}:`, error)
    }
  }

  const removeTokenCookie = (cookieName: string): void => {
    try {
      Cookies.remove(cookieName, {
        domain: env.authCookieDomain,
        secure: env.isProduction,
        sameSite: 'lax',
      })
    } catch (error) {
      console.warn(`Failed to remove cookie ${cookieName}:`, error)
    }
  }

  const loadTokensFromCookies = (): void => {
    try {
      let accessTokenValue = getTokenFromCookie('auth._token.local')
      const refreshTokenValue = getTokenFromCookie('auth._refresh_token.local')

      if (accessTokenValue && accessTokenValue.startsWith('Bearer ')) {
        accessTokenValue = accessTokenValue.replace('Bearer ', '')
      }

      if (validateTokenFormat(accessTokenValue)) {
        accessToken.value = accessTokenValue
      }

      if (refreshTokenValue) {
        refreshToken.value = refreshTokenValue
      }
    } catch (error) {
      console.error('Failed to load tokens from cookies:', error)
      clearAuthState()
    }
  }

  const refreshAccessToken = async (): Promise<string> => {
    if (refreshPromise) {
      return refreshPromise
    }

    if (!refreshToken.value) {
      throw new Error('No refresh token available')
    }

    refreshPromise = (async () => {
      try {
        isLoading.value = true
        error.value = null

        const response = await apiClient.put<RefreshTokenResponse>(
          '/api/clients/v1/refresh',
          {
            refresh_token: refreshToken.value,
          },
          {
            timeout: 10000,
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )

        if (response.data?.access_token) {
          accessToken.value = response.data.access_token
          setTokenCookie(
            'auth._token.local',
            `Bearer ${response.data.access_token}`
          )

          if (response.data.refresh_token) {
            refreshToken.value = response.data.refresh_token
            setTokenCookie(
              'auth._refresh_token.local',
              response.data.refresh_token
            )
          }

          return response.data.access_token
        } else {
          throw new Error('Invalid refresh response')
        }
      } catch (error: any) {
        console.error('Token refresh failed:', error)

        // Only clear auth state for authentication errors, not server errors
        const status = error.response?.status
        const isAuthError = status === 401 || status === 403

        if (isAuthError) {
          clearAuthState()
          removeTokenCookie('auth._token.local')
          removeTokenCookie('auth._refresh_token.local')
        }

        throw error
      } finally {
        isLoading.value = false
        refreshPromise = null
      }
    })()

    return refreshPromise
  }

  const getValidToken = async (): Promise<string | null> => {
    try {
      if (!accessToken.value) {
        loadTokensFromCookies()
      }

      if (!accessToken.value) {
        return null
      }

      if (isTokenExpired.value) {
        try {
          return await refreshAccessToken()
        } catch (error) {
          console.error('Failed to refresh expired token:', error)
          return null
        }
      }

      if (isTokenExpiringSoon(accessToken.value)) {
        try {
          return await refreshAccessToken()
        } catch (error) {
          console.warn(
            'Failed to refresh expiring token, using current token:',
            error
          )
          return accessToken.value
        }
      }

      return accessToken.value
    } catch (error) {
      console.error('Error getting valid token:', error)
      return null
    }
  }

  const fetchUser = async (retryCount = 0): Promise<void> => {
    const MAX_RETRIES = 3
    const RETRY_DELAY = 1000

    try {
      const token = await getValidToken()
      if (!token) {
        throw new Error('No valid token available')
      }

      isLoading.value = true
      error.value = null

      const response = await apiClient.get<{ user: User }>(
        '/api/clients/v1/me',
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000,
        }
      )

      if (response.data?.user) {
        user.value = response.data.user
      } else {
        throw new Error('Invalid user response format')
      }
    } catch (error: any) {
      console.error('Failed to fetch user:', error)

      // Handle different error types
      const status = error.response?.status
      const isNetworkError = !error.response
      const isServerError = status >= 500
      const isClientError = status >= 400 && status < 500

      // Only retry for network errors or temporary server errors (not 401, 403, 404, etc.)
      const shouldRetry =
        retryCount < MAX_RETRIES &&
        (isNetworkError || isServerError) &&
        status !== 401

      if (shouldRetry) {

        await new Promise(resolve =>
          setTimeout(resolve, RETRY_DELAY * (retryCount + 1))
        )
        return fetchUser(retryCount + 1)
      }

      // Handle authentication errors
      if (status === 401) {

        clearAuthState()
        removeTokenCookie('auth._token.local')
        removeTokenCookie('auth._refresh_token.local')
        error.value = 'Authentication failed. Please log in again.'
        throw error
      }

      // Handle server errors
      if (isServerError) {
        console.error(
          `Server error ${status}: The server is experiencing issues. Please try again later.`
        )
        error.value = 'Server error. Please try again later.'
        throw error
      }

      // Handle client errors
      if (isClientError) {
        console.error(`Client error ${status}: ${error.message}`)
        error.value = `Request failed: ${error.message}`
        throw error
      }

      // Handle network errors
      if (isNetworkError) {
        console.error('Network error: Unable to connect to the server')
        error.value =
          'Network error. Please check your connection and try again.'
        throw error
      }

      // Fallback error handling
      error.value = error.message || 'Failed to fetch user'
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const logout = (): void => {
    clearAuthState()
    removeTokenCookie('auth._token.local')
    removeTokenCookie('auth._refresh_token.local')

  }

  const redirectToLogin = (): void => {
    const currentUrl = encodeURIComponent(window.location.href)
    const loginUrl = `${env.fleshDomain}/login?redirect=${currentUrl}`
    window.location.href = loginUrl
  }

  const checkAuthAndRedirect = async (): Promise<void> => {
    try {
      const token = await getValidToken()
      
      if (!token) {
        redirectToLogin()
        return
      }

      // Check if we need to fetch user data
      if (!user.value) {
        await fetchUser()
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      redirectToLogin()
    }
  }

  return {
    // Computed properties
    isAuthenticated,
    isTokenExpired,
    tokenExpiresAt,

    // Reactive state
    user: readonly(user),
    isLoading: readonly(isLoading),
    error: readonly(error),

    // Methods
    getValidToken,
    fetchUser,
    logout,
    redirectToLogin,
    checkAuthAndRedirect,
    refreshAccessToken,
    loadTokensFromCookies,
  }
}
